import type {
  BaseRange,
  Descendant,
  EditorAboveOptions,
  EditorAfterOptions,
  EditorNodesOptions,
  Location,
  NodeEntry,
} from 'slate';
import { createEditor, Editor, Node, Path, Point, Range, Text } from 'slate';

import type { Length, RequireSome } from '../../types';
import { filterBy } from '../../util';
import type {
  AlineBlockElement,
  AlineElement,
  AlineText,
  AtOrAboveOptions,
  Edges,
  FakeElement,
  FooterType,
  HeaderType,
  NodeMatch,
  ParagraphElement,
  RenderModelAncestor,
  RenderModelBlock,
  RenderModelContentBlock,
  RenderModelDocument,
  RenderModelDynamicBlock,
  RenderModelEditor,
  RenderModelFooter,
  RenderModelFootersElement,
  RenderModelHeader,
  RenderModelHeaderOrFooterElement,
  RenderModelHeadersElement,
  RenderModelLine,
  RenderModelNode,
  RenderModelPage,
  RenderModelParagraph,
  RMContainerNode,
  RMDocProps,
  SelectionAffinity,
} from '../internal';
import {
  isCheckboxElement,
  isDrawingElement,
  isDynamicBlockElement,
  isDynamicBlockEntry,
  isImageElement,
  isParagraphElement,
  isParagraphEntry,
  isRenderModelElement,
  isRenderModelLine,
  isRenderModelPage,
  isRenderModelParagraph,
  isRenderModelParagraphEntry,
  isRenderModelTab,
  isRenderModelTableCell,
  isRenderModelText,
  isRMContainerNode,
  isRMFootersElement,
  isRMHeadersElement,
  isTabElement,
  isTableCellEntry,
  measureText,
  Queries,
} from '../internal';

export type TopLevelRMNode =
  | RenderModelPage
  | RenderModelHeadersElement
  | RenderModelFootersElement;

const renderModelQueries = {
  /**
   * Returns the adjusted height of a line taking into account paragraph spacing.
   */
  adjustedLineHeight(
    line: RenderModelLine,
    spacingAfterPx: number,
    spacingBeforePx: number
  ): number {
    return line.isFirstLine && line.isLastLine
      ? line.heightPx + spacingAfterPx + spacingBeforePx
      : line.isFirstLine
        ? line.heightPx + spacingBeforePx
        : line.isLastLine
          ? line.heightPx + spacingAfterPx
          : line.heightPx;
  },

  /**
   * Construct a render-model editor object that can be used for querying the
   * render model to find nodes, etc.
   */
  buildRenderModelEditor(editor: Editor): RenderModelEditor {
    if (!editor.renderModel) throw new Error('Expected renderModel.');

    // Create a fresh editor object so that query methods point to the correct editor's children.
    const renderModelEditor = createEditor();

    renderModelEditor.children = [
      { ...editor.renderModel },
      { ...editor.renderModel.headers },
      { ...editor.renderModel.footers },
    ] satisfies RenderModelEditor['children'];
    renderModelEditor.type = editor.type;
    renderModelEditor.selection = editor.selection;
    renderModelEditor.documentInfo = editor.documentInfo;
    renderModelEditor.measureText = editor.measureText;
    renderModelEditor.renderModel = editor.renderModel;
    renderModelEditor.userState = editor.userState;

    return renderModelEditor as RenderModelEditor;
  },

  /**
   * Given a render model path, gets the absolute content model path.
   */
  getCMPathFromRMPath(editor: Editor, rMPath: Path): Path {
    const levels = Queries.renderModelLevels<RenderModelNode>(editor, {
      at: rMPath,
      voids: true,
    });

    let cMPath: Path = [];

    for (const rMNodeEntry of levels) {
      const [rMNode, rMNodePath] = rMNodeEntry;

      if (!('relativePath' in rMNode)) continue;
      const { relativePath } = rMNode;

      /**
       * TODO: Text nodes inside inline elements have relativePaths relative to the line rather than
       * their parent. This is a bug according to Brent, so we should fix this by migrating to
       * relativeIndex instead of relativePath.
       * https://linear.app/aline/issue/EDI-251/migrate-from-relativepath-to-relativeindex
       */
      const isParagraphRelativeInlinePath =
        relativePath.length === 2 &&
        (isRenderModelText(rMNode) ||
          isCheckboxElement(rMNode) ||
          isTabElement(rMNode) ||
          isDrawingElement(rMNode));

      if (isParagraphRelativeInlinePath) {
        cMPath.push(...relativePath.slice(-1));
        continue;
      }

      /**
       * If we encounter a non-first cell of a vertically merged cell, correct the path to point to
       * the first cell of the vertically merged cell.
       *
       * Since children of the first cell of the original vertically merged cell can be split and
       * partly relocated to the first cell of the subsequent page's vertically merged cell, these
       * children's relativePath can be incorrect, which results in an invalid path if this is not
       * accounted for.
       *
       * We make an exception for when the cell itself is the node whose render model path we're in
       * the process of converting to a content model path. This is to ensure that the content model
       * path can be fetched for any render model table cell, including non-first vertically merged
       * cells.
       */
      if (isRenderModelTableCell(rMNode) && !Path.equals(rMNodePath, rMPath)) {
        const cMTableCellEntry = Editor.node(
          editor,
          Queries.getCMPathFromRMPath(editor, rMNodePath)
        );
        if (!isTableCellEntry(cMTableCellEntry)) throw new Error('Expected table cell entry');

        const [, correctedCMTableCellPath] = Queries.getVerticallyMergedTableCellStart(
          editor,
          cMTableCellEntry
        );

        cMPath = correctedCMTableCellPath.slice();
        continue;
      }

      cMPath.push(...relativePath);
    }

    return cMPath;
  },

  /**
   * Gets the current page number based on the editor selection
   */
  getCurrentPageNumber(editor: Editor): number | null {
    if (!editor.selection) return null;
    return Queries.getPageNumberForCMPoint(editor, editor.selection.focus);
  },

  /**
   * Find the path of the first render model node that corresponds to the content model node at the
   * given path.
   */
  getFirstRMPathFromCMPath(editor: Editor, cMPath: Path): Path | null {
    return Queries.getRMPathsFromCMPath(editor, cMPath).next().value ?? null;
  },

  /**
   * Determines whether a RenderModelDynamicBlock is collapsed.
   * Same as getIsCollapsedDynamicBlock, but for the render model.
   */
  getIsCollapsedRMDynamicBlock(
    editor: Editor,
    options: { rMDynamicBlockEntry: NodeEntry<RenderModelDynamicBlock> }
  ): boolean {
    const { rMDynamicBlockEntry } = options;
    const [, rMDynamicBlockPath] = rMDynamicBlockEntry;
    const cMPath = Queries.getCMPathFromRMPath(editor, rMDynamicBlockPath);
    const dynamicBlockEntry = Editor.node(editor, cMPath);
    if (!isDynamicBlockEntry(dynamicBlockEntry)) return false;

    return Queries.getIsCollapsedDynamicBlock(editor, { dynamicBlockEntry });
  },

  /**
   * Determines whether a RenderModelDynamicBlock is collapsible
   * Same as getIsCollapsibleDynamicBlock, but for the render model.
   */
  getIsCollapsibleRMDynamicBlock(
    editor: Editor,
    options: { rMDynamicBlockEntry: NodeEntry<RenderModelDynamicBlock> }
  ): boolean {
    const { rMDynamicBlockEntry } = options;
    const [, rMDynamicBlockPath] = rMDynamicBlockEntry;
    const cMPath = Queries.getCMPathFromRMPath(editor, rMDynamicBlockPath);
    const [dynamicBlock] = Editor.node(editor, cMPath);
    if (!isDynamicBlockElement(dynamicBlock)) return false;

    return Queries.getIsCollapsibleDynamicBlock(dynamicBlock);
  },

  /**
   * Returns the *content model* Points of a line.
   */
  getLineCMPoints(cMParaPath: Path, rMLine: RenderModelLine): { end: Point; start: Point } {
    const absoluteStartPoint: Point = {
      ...rMLine.relativeStartPoint,
      path: [...cMParaPath, ...rMLine.relativeStartPoint.path],
    };
    const absoluteEndPoint: Point = {
      ...rMLine.relativeEndPoint,
      path: [...cMParaPath, ...rMLine.relativeEndPoint.path],
    };

    return { end: absoluteEndPoint, start: absoluteStartPoint };
  },

  /**
   * Returns the *content model* Range of a line.
   */
  getLineCMRange(cMParaPath: Path, rMLine: RenderModelLine): Range {
    const { end, start } = Queries.getLineCMPoints(cMParaPath, rMLine);

    return { anchor: start, focus: end };
  },

  /**
   * Get the distance to the left edge of the rMLine measured from the left edge of the page.
   */
  getLineLeftEdgePx(editor: Editor, rMLineEntry: NodeEntry<RenderModelLine>): number {
    const [rMLine, rMLinePath] = rMLineEntry;
    const rMParaEntryAboveLine = Queries.renderModelAbove<RenderModelParagraph>(editor, {
      at: rMLinePath,
      match: isParagraphElement,
    });
    if (!rMParaEntryAboveLine) throw new Error('Paragraph must be above line.');

    const [rMPara] = rMParaEntryAboveLine;
    const { leftEdge: leftEdgeRMParaPx } = Queries.getRMBlockHorizontalEdges(
      editor,
      rMParaEntryAboveLine,
      'px'
    );
    const { leftOffsetPx: rMLineLeftOffsetPx } = Queries.lineOffsetLeftPx(editor, {
      paraProps: rMPara,
      rMLine,
    });
    const lineLeftEdgePx = leftEdgeRMParaPx + rMLineLeftOffsetPx;

    return lineLeftEdgePx;
  },

  /**
   * Get the page element corresponding to a given page number.
   */
  getPageForNumber(editor: Editor, pageNum: number): NodeEntry<RenderModelPage> | null {
    return (
      Queries.renderModelNodes(editor, {
        at: [],
        match: (node): node is RenderModelPage =>
          isRenderModelPage(node) && node.pageNum === pageNum,
        // PERF: Do not look for pages inside other pages, headers or footers
        pass: ([node]) => isRMContainerNode(node),
      }).next().value ?? null
    );
  },

  /**
   * Get the page element corresponding to a given render model path. If the path is inside a header
   * or footer, this returns the page this header or footer belongs to.
   */
  getPageForRMPath(editor: Editor, rMPath: Path): NodeEntry<RenderModelPage> | null {
    const rMPageEntry = Queries.renderModelAbove(editor, {
      at: rMPath,
      match: isRenderModelPage,
    });
    if (rMPageEntry) return rMPageEntry;

    const pageNumber = Queries.getPageNumberForRMPath(editor, rMPath);
    if (pageNumber === null) return null;

    return Queries.getPageForNumber(editor, pageNumber);
  },

  /**
   * Gets the page number for a content model point, or null if the point is inside a header or
   * footer.
   */
  getPageNumberForCMPoint(editor: Editor, point: Point): number | null {
    /**
     * Find the render model paragraph containing this point. If the point is in a header or footer,
     * this will return an arbitrary paragrpah inside a header or footer (not necessarily
     * corresponding to any specific page), but this doesn't matter since we return null for
     * paragraphs that aren't descendants of page elements.
     */
    const rMParaEntry = Queries.getRMParagraphsContainingPoint(editor, { point }).next().value;
    if (!rMParaEntry) return null;

    const [, rMParaPath] = rMParaEntry;
    const pageEntry = Queries.renderModelAbove<RenderModelPage>(editor, {
      at: rMParaPath,
      match: isRenderModelPage,
    });
    if (!pageEntry) return null;

    const [page] = pageEntry;
    return page.pageNum;
  },

  /**
   * Get the page number corresponding to a given render model path. If the path is inside a header
   * or footer, this returns the number of the page this header or footer belongs to.
   */
  getPageNumberForRMPath(editor: Editor, rMPath: Path): number | null {
    const topLevelRMEntry = Queries.renderModelAbove(editor, {
      at: rMPath,
      match: (n): n is TopLevelRMNode =>
        isRenderModelPage(n) || isRMHeadersElement(n) || isRMFootersElement(n),
    });
    if (!topLevelRMEntry) return null;
    const [topLevelElement, topLevelPath] = topLevelRMEntry;
    if (isRenderModelPage(topLevelElement)) return topLevelElement.pageNum;

    /**
     * The path of the node relative to the headers or footers element. The index within this top-
     * level container corresponds to the page index.
     */
    const relativePath = Path.relative(rMPath, topLevelPath);
    const indexInTopLevel = relativePath.at(0);
    if (indexInTopLevel === undefined) return null;

    return indexInTopLevel + 1;
  },

  /**
   * Returns the first paragraph entry and its *absolute* path within the provided root node.
   */
  getParagraphEntryInRoot<T extends ParagraphElement | RenderModelParagraph>(
    rootEntry: NodeEntry<T extends ParagraphElement ? AlineBlockElement : RenderModelBlock>,
    options: {
      item?: 'first' | 'last';
    } = {}
  ): NodeEntry<T> | null {
    const { item = 'first' } = options;
    const [node, path] = rootEntry;
    const firstMatchEntry = Queries.getParagraphInRoot(node, { item });
    if (!firstMatchEntry) return null;

    // firstMatchPath is *relative* to its root node so we need to concat it w/ the root node path to get an absolute path.
    const [firstMatch, firstMatchPath] = firstMatchEntry;

    return [firstMatch, [...path, ...firstMatchPath]];
  },

  /**
   * Returns the first paragraph and its *relative* path within the provided root node.
   */
  getParagraphInRoot<T extends ParagraphElement | RenderModelParagraph>(
    root: T extends ParagraphElement ? AlineBlockElement : RenderModelBlock,
    options: {
      item?: 'first' | 'last';
    } = {}
  ): NodeEntry<T> | null {
    const { item = 'first' } = options;
    const entries = Node.nodes(root, { reverse: item === 'last' });

    for (const entry of entries) {
      if (isParagraphEntry(entry)) return entry as NodeEntry<T>;
    }

    return null;
  },

  /**
   * Get the distance to the edges of the block measured from the left edge of
   * the page.
   * @deprecated Prefer using getRMNodeAbsolutePosition directly
   */
  getRMBlockHorizontalEdges(
    editor: Editor,
    rMNodeEntry: NodeEntry<RenderModelContentBlock>,
    unit: Length.Unit = 'px'
  ): Edges {
    const { xLeft, xRight } = Queries.getRMNodeAbsolutePosition(editor, rMNodeEntry, unit);
    return { leftEdge: xLeft, rightEdge: xRight };
  },

  getRMContainerEntryForTarget: (
    editor: Editor,
    options: {
      pageNum: number;
      targetPath: Path;
    }
  ): NodeEntry<RMContainerNode> | null => {
    const { pageNum, targetPath } = options;
    const pageIndex = pageNum - 1;
    const topLevelRMEntry = Queries.renderModelAbove(editor, {
      at: targetPath,
      match: (n): n is TopLevelRMNode =>
        isRenderModelPage(n) || isRMHeadersElement(n) || isRMFootersElement(n),
    });
    if (!topLevelRMEntry) return null;

    const [topLevelRMNode, topLevelRMPath] = topLevelRMEntry;

    switch (topLevelRMNode.type) {
      case 'page':
        return [topLevelRMNode, topLevelRMPath];
      case 'headers':
        return [topLevelRMNode.children[pageIndex], [...topLevelRMPath, pageIndex]];
      case 'footers':
        return [topLevelRMNode.children[pageIndex], [...topLevelRMPath, pageIndex]];
    }
  },

  /**
   * Get a render model footer entry by type.
   */
  getRMFooterEntry: (editor: Editor, footerType: FooterType): NodeEntry<RenderModelFooter> => {
    const [rMFooters, rMFootersPath] = Queries.getRMFootersElement(editor);
    const rMFooterIndex = rMFooters.children.findIndex(n => n.type === footerType);
    if (rMFooterIndex === -1) throw new Error('Expected to find footer.');

    const rMFooter = rMFooters.children[rMFooterIndex];

    return [rMFooter, [...rMFootersPath, rMFooterIndex]];
  },

  /**
   * Return the render model footers element.
   */
  getRMFootersElement: (editor: Editor): NodeEntry<RenderModelFootersElement> => {
    const rMEditor = Queries.buildRenderModelEditor(editor);
    const rMFootersIndex = rMEditor.children.findIndex(isRMFootersElement);
    if (rMFootersIndex === -1) throw new Error('Expected render model footers.');

    const rMFooters = rMEditor.children[rMFootersIndex];
    if (!isRMFootersElement(rMFooters)) throw new Error('Expected render model footers.');

    return [rMFooters, [rMFootersIndex]];
  },

  /**
   * Get a render model header entry by type.
   */
  getRMHeaderEntry: (editor: Editor, headerType: HeaderType): NodeEntry<RenderModelHeader> => {
    const [rMHeaders, rMHeadersPath] = Queries.getRMHeadersElement(editor);
    const rMHeaderIndex = rMHeaders.children.findIndex(n => n.type === headerType);
    if (rMHeaderIndex === -1) throw new Error('Expected to find header.');

    const rMHeader = rMHeaders.children[rMHeaderIndex];

    return [rMHeader, [...rMHeadersPath, rMHeaderIndex]];
  },

  /**
   * Get the active render model header entry on a given page.
   */
  getRMHeaderOrFooterEntryOnPage<T extends 'header' | 'footer'>(
    editor: Editor,
    options: {
      pageNumber: number;
      rMDocProps: RMDocProps;
      type: T;
    }
  ): NodeEntry<RenderModelHeaderOrFooterElement<T>> {
    const { pageNumber, rMDocProps, type } = options;
    const isHeader = type === 'header';
    const [, rMPartPath] = isHeader
      ? Queries.getRMHeadersElement(editor)
      : Queries.getRMFootersElement(editor);

    const element = rMDocProps[isHeader ? 'headers' : 'footers'].children[
      pageNumber - 1
    ] as RenderModelHeaderOrFooterElement<T>;
    if (!element) throw new Error(`Expected header for page ${pageNumber}.`);

    return [element, [...rMPartPath, pageNumber - 1]];
  },

  /**
   * Return the render model headers element.
   */
  getRMHeadersElement: (editor: Editor): NodeEntry<RenderModelHeadersElement> => {
    const rMEditor = Queries.buildRenderModelEditor(editor);
    const rMHeadersIndex = rMEditor.children.findIndex(isRMHeadersElement);
    if (rMHeadersIndex === -1) throw new Error('Expected render model headers.');

    const rMHeaders = rMEditor.children[rMHeadersIndex];
    if (!isRMHeadersElement(rMHeaders)) throw new Error('Expected render model headers.');

    return [rMHeaders, [rMHeadersIndex]];
  },

  /**
   * Get the lines that contain the given point. This will usually return a single line, except for
   * points inside headers or footers.
   */
  *getRMLinesAboveCMPoint(
    editor: Editor,
    cMPoint: Point,
    affinity: SelectionAffinity
  ): Generator<NodeEntry<RenderModelLine>> {
    for (const rMPoint of Queries.getRMPointsFromCMPoint(editor, cMPoint, affinity)) {
      const rMLineEntry = Queries.renderModelAbove(editor, {
        at: rMPoint,
        match: isRenderModelLine,
      });
      if (rMLineEntry) yield rMLineEntry;
    }
  },

  /**
   * Given a set of candidate render model locations, find the first location that is at the page
   * level (i.e. not inside a header or footer), or which is inside a header or footer matching a
   * given page number.
   *
   * Use this to narrow down a set of locations returned by queries such as
   * Queries.getRMRangesFromCMRange, which can return multiple locations for content model locations
   * inside a header or footer (since each page has its own version of the header or footer).
   */
  getRMLocationForActiveHeaderOrFooter<T extends Location>(
    editor: Editor,
    candidates: Iterable<T>,
    headerOrFooterPageNum: number | null
  ): T | null {
    return (
      Queries.getRMLocationsForActiveHeaderOrFooter(
        editor,
        candidates,
        headerOrFooterPageNum
      ).next().value ?? null
    );
  },

  /**
   * The same filter as getRMLocationForActiveHeaderOrFooter, but yielding every candidate that
   * survives it rather than only the first. Use this when a node legitimately has more than one
   * page-level location, such as a table cell split over a page break.
   */
  *getRMLocationsForActiveHeaderOrFooter<T extends Location>(
    editor: Editor,
    candidates: Iterable<T>,
    headerOrFooterPageNum: number | null
  ): Generator<T> {
    for (const location of candidates) {
      const rMPageEntry = Queries.renderModelNodes(editor, {
        at: location,
        match: isRenderModelPage,
      }).next().value;

      // If the location is on one or more pages, return it
      if (rMPageEntry) {
        yield location;
        continue;
      }

      const paths: Path[] = Range.isRange(location)
        ? [location.anchor.path, location.focus.path]
        : Point.isPoint(location)
          ? [location.path]
          : [location];

      // Otherwise, only return the location if it belongs to the active header or footer
      if (
        headerOrFooterPageNum !== null &&
        paths.every(path => Queries.isRMPathOnPage(editor, path, headerOrFooterPageNum))
      )
        yield location;
    }
  },

  /**
   * Get a render model node by its path.
   */
  getRMNode: (editor: Editor, path: Path): RenderModelNode => {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Node.get(renderModelEditor, path) as RenderModelNode;
  },

  /**
   * Get the content model Range of a render model node by looking at the start and end points of
   * all paragraphs in that node.
   */
  getRMNodeRange(editor: Editor, [, rMNodePath]: NodeEntry<RenderModelBlock>): Range | null {
    let minStartPoint: Point | null = null;
    let maxEndPoint: Point | null = null;

    /**
     * An earlier version of this function considered only the first and last render model
     * paragraphs. However, since the order of render model paragraphs can differ from that of
     * content model paragraphs, it is necessary to consider all paragraphs.
     */
    for (const rMParaEntry of Queries.renderModelNodes(editor, {
      at: rMNodePath,
      match: isRenderModelParagraph,
    })) {
      const { endPoint, startPoint } = Queries.getRMParaCMLocations(editor, rMParaEntry);
      if (!minStartPoint || Point.isBefore(startPoint, minStartPoint)) minStartPoint = startPoint;
      if (!maxEndPoint || Point.isAfter(endPoint, maxEndPoint)) maxEndPoint = endPoint;
    }

    if (!minStartPoint || !maxEndPoint) return null;
    return { anchor: minStartPoint, focus: maxEndPoint };
  },

  /**
   * Gets the content model locations of a render model paragraph.
   * Note: This gives the true locations of the render model paragraph b/c it
   * can be split across pages.
   */
  getRMParaCMLocations(
    editor: Editor,
    rMParaEntry: NodeEntry<RenderModelParagraph>
  ): {
    cMParaPath: Path;
    endPoint: Point;
    startPoint: Point;
  } {
    const [rMPara, rMParaPath] = rMParaEntry;
    const cMParaPath = Queries.getCMPathFromRMPath(editor, rMParaPath);
    const firstLine = rMPara.children[0];
    const lastLine = rMPara.children[rMPara.children.length - 1];

    return {
      cMParaPath,
      endPoint: {
        ...lastLine.relativeEndPoint,
        path: [...cMParaPath, ...lastLine.relativeEndPoint.path],
      },
      startPoint: {
        ...firstLine.relativeStartPoint,
        path: [...cMParaPath, ...firstLine.relativeStartPoint.path],
      },
    };
  },

  /**
   * Find the paragraph(s) containing a given content model point. The only time when multiple
   * paragraphs are returned should be when the point is inside a header or footer, since the same
   * content model paragraph can exist in multiple render model headers or footers.
   */
  *getRMParagraphsContainingPoint(
    editor: Editor,
    options: {
      point: Point;
    }
  ): Generator<NodeEntry<RenderModelParagraph>, void, undefined> {
    const { point } = options;

    const cMParagraphEntry = Editor.above(editor, { at: point, match: isParagraphElement });
    if (!cMParagraphEntry) return;
    const [, cMPath] = cMParagraphEntry;

    for (const rMPath of Queries.getRMPathsFromCMPath(editor, cMPath)) {
      const rMParagraphEntry = Queries.renderModelNode(editor, rMPath);
      if (!isRenderModelParagraphEntry(rMParagraphEntry)) throw new Error('Expected paragraph');

      const { endPoint, startPoint } = Queries.getRMParaCMLocations(editor, rMParagraphEntry);
      const containsPoint = Range.includes({ anchor: startPoint, focus: endPoint }, point);

      if (containsPoint) yield rMParagraphEntry;
    }
  },

  /**
   * Given a content model path, gets the set of corresponding render model paths.
   */
  getRMPathsFromCMPath(
    editor: Editor,
    cMPath: Path,
    options: { reverse?: boolean } = {}
  ): Generator<Path> {
    const { reverse = false } = options;
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    /**
     * Render model texts are not currently supported. This can be added once the issue where some
     * text nodes have relativePaths of length 2 is fixed, or after migrating to relativeIndex.
     */
    if (Text.isText(Node.get(editor, cMPath)))
      throw new Error('Text nodes are not currently supported by getRMPathsFromCMPath');

    /**
     * Recursively traverse the render model editor, descending into any node whose relative path
     * matches the relative target path and yielding any node where the relative path matches
     * exactly.
     */
    function* findInParent(parentRMPath: Path, relativeTargetPath: Path): Generator<Path> {
      if (relativeTargetPath.length === 0) {
        yield parentRMPath;
        return;
      }

      for (const [node, rMPath] of Node.children(renderModelEditor, parentRMPath, { reverse })) {
        const rMNode = node as RenderModelNode;
        if (!isRenderModelElement(rMNode)) continue;

        const relativePath = 'relativePath' in rMNode ? rMNode.relativePath : null;

        if (
          !relativePath ||
          Path.equals(relativePath, relativeTargetPath) ||
          Path.isAncestor(relativePath, relativeTargetPath)
        )
          yield* findInParent(rMPath, Path.relative(relativeTargetPath, relativePath ?? []));
      }
    }

    return findInParent([], cMPath);
  },

  /**
   * Get the render model points corresponding to the given content model point. This will usually
   * return a single point, except for points inside headers or footers.
   */
  *getRMPointsFromCMPoint(
    editor: Editor,
    cMPoint: Point,
    affinity: SelectionAffinity
  ): Generator<Point, void, undefined> {
    const cMParagraphEntry = Editor.above(editor, { at: cMPoint, match: isParagraphElement });
    if (!cMParagraphEntry) return;
    const [, cMParagraphPath] = cMParagraphEntry;

    for (const [, rMParagraphPath] of Queries.getRMParagraphsContainingPoint(editor, {
      point: cMPoint,
    })) {
      const relativePath = Path.relative(cMPoint.path, cMParagraphPath);

      const rMTextEntries = Array.from(
        Queries.renderModelNodes(editor, {
          at: rMParagraphPath,
          match: isRenderModelText,
        })
      );

      let lastMatchingPoint: Point | null = null;

      for (const [rMText, rMTextPath] of rMTextEntries) {
        const { relativeEndPoint, relativeStartPoint } = rMText;
        if (!Path.equals(relativePath, relativeStartPoint.path)) continue;

        const startOffset = relativeStartPoint.offset;
        const endOffset = relativeEndPoint.offset;
        if (endOffset < cMPoint.offset) continue;

        lastMatchingPoint = { offset: cMPoint.offset - startOffset, path: rMTextPath };

        /**
         * If we're at the end of the text node and the affinity is forward, continue checking text
         * nodes in case there's a better match. Otherwise, we settle on the point in this text node.
         */
        const skipDueToAffinity = endOffset === cMPoint.offset && affinity === 'forward';
        if (!skipDueToAffinity) break;
      }

      if (lastMatchingPoint) yield lastMatchingPoint;
    }
  },

  /**
   * Get the render model ranges corresponding to the given content model range. This will usually
   * return a single range, except for ranges inside headers or footers.
   */
  *getRMRangesFromCMRange<T extends BaseRange & { affinity?: SelectionAffinity }>(
    editor: Editor,
    cMRange: T
  ): Generator<T, void, undefined> {
    const { affinity = 'forward', anchor, focus } = cMRange;

    const rMAnchors = Queries.getRMPointsFromCMPoint(editor, anchor, affinity);
    const rMFoci = Queries.getRMPointsFromCMPoint(editor, focus, affinity);

    for (const rMAnchor of rMAnchors) {
      const rMFocus = rMFoci.next().value;
      if (!rMFocus) return;
      yield { ...cMRange, anchor: rMAnchor, focus: rMFocus };
    }
  },

  getRenderModelDoc(editor: Editor): RenderModelDocument {
    return editor.renderModel as RenderModelDocument;
  },

  /**
   * Returns the width of the text in the line.
   * Taking into account the spaceWidthPx of the line.
   */
  getTextWidthPx: (
    editor: Editor,
    options: {
      isLastNodeInLine: boolean;
      rMLine: Pick<RenderModelLine, 'spaceWidthPx'>;
      textNode: AlineText;
      offset?: {
        end: number;
        start: number;
      };
    }
  ): number => {
    const { isLastNodeInLine, offset, rMLine, textNode } = options;
    const isOffsetAtEndOfLine = !offset || offset.end === textNode.text.length;
    const maybeSlicedString = offset
      ? textNode.text.slice(offset.start, offset.end)
      : textNode.text;

    if (rMLine.spaceWidthPx === 'default' || !isFinite(rMLine.spaceWidthPx))
      return measureText(editor, {
        suppressJustifiedWarning: true,
        text: maybeSlicedString,
        textNode,
      });

    /**
     * If it's the last text node in the line, the spaces after the last word do NOT get the rMLine.spaceWidthPx
     * and are "default spaces".
     */
    const maybeTrimmedTextString =
      isLastNodeInLine && isOffsetAtEndOfLine ? maybeSlicedString.trimEnd() : maybeSlicedString;
    const spacesAtEnd =
      isLastNodeInLine && isOffsetAtEndOfLine
        ? maybeSlicedString.slice(maybeTrimmedTextString.length)
        : '';
    const words = maybeTrimmedTextString.split(' ');
    const totalSpacesInWords = words.length - 1;
    // This will return 0 if it's not the last node/there are no spaces at the end.
    const spacesAtEndWidthPx = measureText(editor, {
      suppressJustifiedWarning: true,
      text: spacesAtEnd,
      textNode,
    });
    const totalSpacesWidthPx = totalSpacesInWords * rMLine.spaceWidthPx + spacesAtEndWidthPx;

    // Calculate the width of the words.
    const totalWordWidth = words.reduce((acc, word) => {
      const wordWidth = measureText(editor, { text: word, textNode });
      return acc + wordWidth;
    }, 0);

    return totalWordWidth + totalSpacesWidthPx;
  },

  /**
   * Check if a render model path belongs to a given page. A path belongs to a page if it's a
   * descendant of the page element or a header or footer specific to that page.
   */
  isRMPathOnPage(editor: Editor, rMPath: Path, pageNum: number): boolean {
    const rMPageEntry = Queries.getPageForRMPath(editor, rMPath);
    if (!rMPageEntry) return false;
    return rMPageEntry[0].pageNum === pageNum;
  },

  /**
   * Determine whether a selection point (e.g., the start/end of the selection or the cursor) is within a RenderModelLine.
   */
  isSelectionPointInLine(args: {
    lineCMEnd: Point;
    lineCMStart: Point;
    rMLine: RenderModelLine;
    selectionAffinity: SelectionAffinity;
    selPoint: Point;
  }): boolean {
    const { lineCMEnd, lineCMStart, rMLine, selPoint, selectionAffinity } = args;
    const isPointInLineRange = Range.includes({ anchor: lineCMStart, focus: lineCMEnd }, selPoint);

    if (!isPointInLineRange) return false;

    const isAtStartOfLineAfterFirst =
      Point.equals(selPoint, lineCMStart) &&
      selectionAffinity === 'backward' &&
      !rMLine.isFirstLine;
    if (isAtStartOfLineAfterFirst) return false;

    const isAtEndOfLineBeforeLast =
      Point.equals(selPoint, lineCMEnd) && selectionAffinity === 'forward' && !rMLine.isLastLine;
    return !isAtEndOfLineBeforeLast;
  },

  nodeFromRMLocation(editor: Editor, at: Location): NodeEntry {
    const rMPath = Editor.path(editor, at);
    const cMPath = Queries.getCMPathFromRMPath(editor, rMPath);
    return Editor.node(editor, cMPath);
  },

  /**
   * Returns the width of content in a line, not accounting for justified text.
   */
  nonJustifiedLineContentWidthPx: (
    editor: Editor,
    line: Pick<RenderModelLine, 'children'>
  ): number => {
    const fakeElement: FakeElement = { children: line.children, suggestedEdits: [], type: 'fake' };

    let widthPx = 0;

    for (const [node] of Node.descendants(fakeElement, {
      /** Do not measure text inside a checkbox. */
      pass: ([node]) => isCheckboxElement(node),
    })) {
      if (Text.isText(node)) {
        widthPx += measureText(editor, {
          suppressJustifiedWarning: true,
          textNode: node,
        });
        continue;
      }

      if (isImageElement(node)) {
        widthPx += Queries.getImageDimensions(node).widthPx;
        continue;
      }

      if (isRenderModelTab(node)) {
        widthPx += node.tabWidthPx;
        continue;
      }

      if (isCheckboxElement(node)) {
        widthPx += Queries.renderedCheckboxSizePx(node.children.at(0) ?? {});
        continue;
      }
    }

    return widthPx;
  },

  /**
   * Get the first node entry at a location.
   */
  rMFirst(editor: Editor, at: Location): ReturnType<typeof Editor.first> {
    const rMEditor = Queries.buildRenderModelEditor(editor);

    return Editor.first(rMEditor, at);
  },

  /**
   * Get the last node entry at a location.
   */
  rMLast(editor: Editor, at: Location): ReturnType<typeof Editor.last> {
    const rMEditor = Queries.buildRenderModelEditor(editor);

    return Editor.last(rMEditor, at);
  },

  /**
   * Same as Queries.pathIsAbutting, but for render model.
   */
  rMPathIsAbutting(editor: Editor, referencePath: Path, path: Path): boolean {
    const rMEditor = Queries.buildRenderModelEditor(editor);

    return Queries.pathIsAbutting(rMEditor, referencePath, path);
  },

  renderModelAbove<T extends RenderModelAncestor>(
    editor: Editor,
    options: EditorAboveOptions<T>
  ): NodeEntry<T> | undefined {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.above<T>(renderModelEditor, options);
  },

  /**
   * Similar to Editor.above, but automatically converts the resulting content model entry to the
   * first corresponding render model entry.
   */
  renderModelAboveCMLocation<T extends AlineElement>(
    editor: Editor,
    options: EditorAboveOptions<T>
  ): NodeEntry<Extract<RenderModelAncestor, { type: T['type'] }>> | undefined {
    const aboveEntry = Editor.above(editor, options);
    if (!aboveEntry) return;
    const [node, path] = aboveEntry;

    const [rMNode, rMPath] = Queries.renderModelNodeFromCMLocation(editor, path);
    if (!isRenderModelElement(rMNode) || rMNode.type !== node.type)
      throw new Error('Mismatched render model element for content model element');

    return [rMNode as Extract<RenderModelAncestor, { type: T['type'] }>, rMPath];
  },

  renderModelAfter(editor: Editor, at: Location, options: EditorAfterOptions = {}) {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.after(renderModelEditor, at, options);
  },

  /**
   * Same as Queries.atOrAbove, but for render model.
   */
  renderModelAtOrAbove<T extends RenderModelAncestor>(
    editor: Editor,
    options: AtOrAboveOptions<T>
  ): NodeEntry<T> | undefined {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Queries.atOrAbove(renderModelEditor, options);
  },

  renderModelEdges(editor: Editor, at: Location) {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.edges(renderModelEditor, at);
  },

  /**
   * Return levels at a given Location in the render model.
   */
  renderModelLevels<T extends Node>(
    editor: Editor,
    options: { at: Location; match?: NodeMatch; reverse?: boolean; voids?: boolean }
  ): Generator<NodeEntry<T>, void, undefined> {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.levels<T>(renderModelEditor, options);
  },

  renderModelNext<T extends Descendant>(
    editor: Editor,
    options: {
      at: Location; // A Location *in the render model*.
      match?: NodeMatch<T>;
      mode?: 'all' | 'highest' | 'lowest';
      voids?: boolean;
    }
  ): NodeEntry<T> | undefined {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.next<T>(renderModelEditor, options);
  },

  renderModelNode(editor: Editor, at: Location): NodeEntry<RenderModelNode> {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.node(renderModelEditor, at) as NodeEntry<RenderModelNode>;
  },

  renderModelNodeFromCMLocation(editor: Editor, at: Location): NodeEntry<RenderModelNode> {
    const cMPath = Editor.path(editor, at);

    const rMPath = Queries.getFirstRMPathFromCMPath(editor, cMPath);
    if (!rMPath)
      throw new Error(
        `Could not get render model element for content model element at path ${JSON.stringify(cMPath)}`
      );

    return Queries.renderModelNode(editor, rMPath);
  },

  /**
   * Get all render model nodes matching a query.
   *
   * Ideally, this would pass straight through to Editor.nodes. However, Node.nodes (which
   * Editor.nodes uses) uses a Set to track visited nodes, on the reasonable assumption that the
   * same node object should not occur more than once.
   *
   * When we build the render model, each page has its own copy of the header or footer, stored as a
   * child of the top-level headers or footers element. The index of each header or footer in this
   * top-level element corresponds to the index of the page it belongs to.
   *
   * Often (but not always), multiple pages will share the same header or footer node object, in
   * which case the same node object will appear multiple times in the render model. Because of
   * this, Editor.nodes will only return the first matching version of each header or footer, and
   * will skip any subsequent headers or footers (or nodes inside them) sharing the same object
   * reference.
   *
   * To account for this, whenever Queries.renderModelNodes encounters a node inside the top-level
   * headers or footers, it checks the equivalent path for all header or footer nodes and also
   * returns any nodes it finds that share an object reference with the originally found node.
   */
  *renderModelNodes<T extends RenderModelNode>(
    editor: Editor,
    options: RequireSome<EditorNodesOptions<T>, 'at'>
  ): Generator<NodeEntry<T>, void, undefined> {
    const { at, reverse } = options;
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    const [, rMHeadersPath] = Queries.getRMHeadersElement(editor);
    const [, rMFootersPath] = Queries.getRMFootersElement(editor);

    const visitedNodes = new Set<Node>();

    for (const rMNodeEntry of Editor.nodes<T>(renderModelEditor, options)) {
      const [rMNode, rMNodePath] = rMNodeEntry;
      const isInHeaders = Path.isDescendant(rMNodePath, rMHeadersPath);
      const isInFooters = Path.isDescendant(rMNodePath, rMFootersPath);

      if (!isInHeaders && !isInFooters) {
        yield rMNodeEntry;
        continue;
      }

      // Make sure we don't locate duplicate nodes for the same node twice
      if (visitedNodes.has(rMNode)) continue;
      visitedNodes.add(rMNode);

      const topLevelRMNodePath = isInHeaders ? rMHeadersPath : rMFootersPath;

      /** The path of the node relative to the specific header or footer */
      const rMRelativePath = rMNodePath.slice(2);

      const rMContainerEntries = Node.children(renderModelEditor, topLevelRMNodePath, {
        reverse,
      });

      /**
       * Check the equivalent path in all headers or footers and yield a result if the node at this
       * path is a duplicate of the one returned by Editor.nodes, assuming that this duplicate node
       * satisfies the at option.
       */
      for (const [, rMContainerPath] of rMContainerEntries) {
        const rMPotentialDuplicatePath = rMContainerPath.concat(rMRelativePath);

        const rMPotentialDuplicateNode = Node.getIf(
          renderModelEditor,
          rMPotentialDuplicatePath
        ) as T;

        if (
          rMPotentialDuplicateNode === rMNode &&
          Queries.pathMatchesLocation(renderModelEditor, rMPotentialDuplicatePath, at)
        )
          yield [rMPotentialDuplicateNode, rMPotentialDuplicatePath];
      }
    }
  },

  renderModelParagraphs(root: Node): Generator<NodeEntry<RenderModelParagraph>, void, undefined> {
    return filterBy(Node.nodes(root), ([node]) => isRenderModelParagraph(node)) as Generator<
      NodeEntry<RenderModelParagraph>,
      void,
      undefined
    >;
  },

  renderModelPrevious<T extends Node>(
    editor: Editor,
    options: {
      at: Location; // A Location *in the render model*.
      match?: NodeMatch<T>;
      mode?: 'all' | 'highest' | 'lowest';
      voids?: boolean;
    }
  ): NodeEntry<T> | undefined {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.previous(renderModelEditor, options);
  },

  renderModelRange(editor: Editor, at: Location) {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.range(renderModelEditor, at);
  },

  renderModelStart(editor: Editor, at: Location) {
    const renderModelEditor = Queries.buildRenderModelEditor(editor);

    return Editor.start(renderModelEditor, at);
  },

  /**
   * Returns the rendered spacing before for a paragraph.
   */
  renderedSpacingBeforePx: (
    rMPara: Pick<
      RenderModelParagraph,
      | 'prevParaSpacingAfterPx'
      | 'spacingBefore'
      | 'spacingBeforeSuppressed'
      | 'suggestedEdits'
      | 'splitIndex'
    >
  ): number => {
    const spacingBeforePx = Queries.renderedParagraphSpacingBefore(rMPara, 'px');
    // If it's a split paragraph, don't render any spacing before.
    if (rMPara.splitIndex !== 0) return 0;
    if (typeof rMPara.prevParaSpacingAfterPx !== 'number') return spacingBeforePx;

    return Math.max(0, spacingBeforePx - rMPara.prevParaSpacingAfterPx);
  },
};

export default renderModelQueries;
